import { useContext } from "react";
import { useSelector } from "react-redux";
import { Boundaries } from "@planzone/core";
import {
  CapacityGranularity,
  CapacitySort,
  Filter,
  ResourceCapacity,
} from "@planzone/dto";
import { CoreContext } from "../context";

interface ViewModel {
  isRequestPending: boolean;
  isRequestSuccess: boolean;
  isRequestFailure: boolean;
  content: ResourceCapacity[];
  currentPage: number;
  canLoadMore: boolean;
  totalElements: number;
  retrieveCapacity(request: Boundaries["retrieveCapacity"]["request"]): void;
}

function useRetrieveCapacity(
  from: string,
  to: string,
  granularity: CapacityGranularity,
  filter: Filter | undefined,
  sort: CapacitySort | undefined,
  size: number,
): ViewModel {
  const { selector, core } = useContext(CoreContext);

  return {
    isRequestPending: useSelector(() =>
      selector.isRetrieveCapacityRequestPending(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestSuccess: useSelector(() =>
      selector.isRetrieveCapacityRequestSuccess(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    isRequestFailure: useSelector(() =>
      selector.isRetrieveCapacityRequestFailure(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    content: useSelector(() =>
      selector.getCapacityContent(from, to, granularity, filter, sort, size),
    ),
    currentPage: useSelector(() =>
      selector.getCapacityCurrentPage(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    canLoadMore: useSelector(() =>
      selector.canLoadMoreCapacity(from, to, granularity, filter, sort, size),
    ),
    totalElements: useSelector(() =>
      selector.getCapacityTotalElements(
        from,
        to,
        granularity,
        filter,
        sort,
        size,
      ),
    ),
    retrieveCapacity: core.retrieveCapacity,
  };
}

export { useRetrieveCapacity };
